import { Fragment, useRef } from 'react';
import BouncingDot from './BouncingDot';
import useScrollProgress from './useScrollProgress';

// Fractions of a viewport height, so the sequence paces the same way on any screen size.
// They sit just past each snap stop (1vh, 2vh) so one block arrives per stop instead of
// both landing on the first. Named, because the bouncing dot moves on the same beats.
const HEADING_AT = 0.5;
const BODY_AT = 1.5;

// Where along each target's width the dot sits — title, heading, bio. Centred for the
// first two, but pushed left for the bio: the heading above it is right-aligned, so its
// last line runs to the right edge and only the left of that gap is clear.
const DOT_ANCHOR = [0.5, 0.5, 0.12];

/**
 * A field colour as the "r, g, b" list `data-header-fill` wants — see page_assets/header.
 * Each landing hands the header its own field, and this keeps that value derived from the
 * colour the panel is actually painted with rather than transcribed by hand beside it.
 */
export function hexToRgbList(hex) {
    const value = parseInt(hex.replace('#', ''), 16);
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255].join(', ');
}

/**
 * The landing banner every page opens on: a 3-viewport scroll track with a pinned panel
 * inside, so scrolling reads as text arriving rather than the page moving. The landing
 * view is just the photo and the title; the heading and bio fade in one at a time as you
 * go, with the dot hopping to whichever block is newest.
 *
 * Below lg none of that applies: the track collapses and the pieces are simply stacked,
 * since there is no room beside the title for a second column and no pinned panel to
 * reveal anything against. The photo keeps exactly one screen there, so the title sits on
 * its floor and the heading and bio under it are found by scrolling.
 *
 * The three landings are the same object down to the type scale — only the picture, the
 * palette and the words differ, so those are all this takes.
 *
 * @param backgroundImage   the landing photo, full-bleed behind everything
 * @param field             the flat colour the photo is printed on, sampled off it. The
 *                          panel is painted this, so the picture's own ground and the
 *                          panel's meet with no edge between them and the subject floats
 *                          in a field running to every edge of the viewport.
 * @param ink               colour of the heading and the bio
 * @param titleInk          colour of the title; inherits from the page when left out
 * @param title             { script, text, syllables, scriptFirst } — one word set in the
 *                          script face and one in the serif, in that order when
 *                          `scriptFirst`. The serif word is whole on mobile (`text`) and
 *                          broken over `syllables` on desktop, where the column is too
 *                          narrow to carry it in one line.
 * @param heading           the heading's copy, where it is plain text
 * @param sections          [{ key, label, onSelect }] — where the heading doubles as the
 *                          page's section nav, its lines instead. Takes precedence.
 * @param body              the bio under the heading
 */
export default function HeroBanner({
    backgroundImage,
    field,
    ink,
    titleInk,
    title,
    heading,
    sections = [],
    body,
}) {
    const { scrollY, viewportHeight } = useScrollProgress();

    const panelRef = useRef(null);
    const titleRef = useRef(null);
    const headingRef = useRef(null);
    const bodyRef = useRef(null);

    // The hidden half of the reveal is lg-only: on mobile every block is on screen from
    // the start, because the scroll depth that would uncover them is spent scrolling
    // through the stack itself.
    const revealAt = (viewportFraction) =>
        `transition-all duration-700 ease-out ${
            scrollY > viewportHeight * viewportFraction
                ? 'opacity-100 translate-y-0'
                : 'opacity-100 lg:opacity-0 lg:translate-y-6 lg:pointer-events-none'
        }`;

    // Whichever block is the newest one on screen — the dot follows it.
    const activeStep = scrollY > viewportHeight * BODY_AT ? 2
        : scrollY > viewportHeight * HEADING_AT ? 1
        : 0;

    // Broken over its syllables only where there is a column narrow enough to want them:
    // on a phone the whole word fits one line and reads as a heading over the photo.
    const serifWord = (
        <>
            <span className="lg:hidden">{title.text}</span>
            <span className="hidden lg:inline">
                {title.syllables.map((syllable, index) => (
                    <Fragment key={syllable}>
                        {index > 0 && <br/>}
                        {syllable}
                    </Fragment>
                ))}
            </span>
        </>
    );

    const scriptWord = (
        <span className="font-normal" style={{ fontFamily: '"Priestacy", cursive' }}>
            {title.script}
        </span>
    );

    return (
        // w-full rather than left to the default: a page whose root is a centred flex
        // column (the art portfolio's is) would otherwise shrink-wrap this track to its
        // content, and the panel's own w-full would only fill that narrow box.
        <div className="relative w-full lg:h-[300vh] snap-start">
            {/* One snap stop per reveal step. Without these the track's only stop is its
                top, and a single gesture would jump straight to the next section. Inert
                on pages that don't turn snapping on, so they're unconditional. */}
            {[1, 2].map((step) => (
                <div
                    key={step}
                    aria-hidden="true"
                    className="hidden lg:block absolute left-0 w-px h-px snap-start pointer-events-none"
                    style={{ top: `${step * 100}vh` }}
                />
            ))}

            <section
                ref={panelRef}
                className="relative w-full min-h-screen lg:h-screen lg:sticky lg:top-0 lg:overflow-hidden flex flex-col items-center"
                style={{ backgroundColor: field }}
            >
                {/* One screen tall on mobile, matching the title column, so the copy below
                    sits on the panel's own field rather than on more photo; the whole
                    panel on desktop. Cropped to fill rather than fitted — each of these
                    photos carries its subject in the middle of the frame, so a crop takes
                    evenly off the flat colour around it. */}
                <div aria-hidden="true" className="absolute inset-x-0 top-0 h-dvh lg:h-full">
                    <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${backgroundImage})` }}
                    />
                    {/* The foot of the photo faded into the field colour, so a figure
                        running off its bottom edge dissolves into the section instead of
                        stopping on a seam; invisible where the photo's own ground already
                        reaches that edge. Mobile only — on desktop the photo is the whole
                        panel and there is no seam to cover. */}
                    <div
                        className="lg:hidden absolute inset-x-0 bottom-0 h-2/5"
                        style={{ backgroundImage: `linear-gradient(to bottom, rgba(${hexToRgbList(field)}, 0), ${field} 85%)` }}
                    />
                </div>

                {/* lg only — below that the blocks sit too close to clear the dot. */}
                <BouncingDot
                    containerRef={panelRef}
                    targetRef={[titleRef, headingRef, bodyRef][activeStep]}
                    anchor={DOT_ANCHOR[activeStep]}
                    className="hidden lg:block"
                />

                {/* No top padding on mobile: the photo starts at the very top of the
                    viewport and runs up behind the header, which is fixed and transparent
                    over every landing. */}
                <div className="relative z-10 w-full max-w-6xl lg:max-w-7xl lg:h-full px-4 sm:px-6 lg:px-8 pb-16 lg:py-24 flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-12">

                    {/* The title, the only thing visible on landing. Exactly one screen
                        tall on mobile, so the photo and the title are the whole of that
                        first view. dvh rather than vh so a phone's retracting address bar
                        can't push the title off the bottom edge. */}
                    <div className="relative h-dvh lg:h-auto lg:w-1/2">
                        {/* Sat on the floor of that first screen on mobile, over the part
                            of the photo the fade has taken down to flat colour, and well
                            clear of the header at the top of it. */}
                        <h1
                            className="absolute inset-x-0 bottom-6 sm:bottom-8 lg:static lg:bottom-auto z-20 text-[15vw] sm:text-[11vw] lg:text-9xl leading-[0.95] lg:leading-none font-bold text-left -mb-2"
                            style={{ fontFamily: '"Zaslia", serif', color: titleInk }}
                        >
                            {/* Inline wrapper so the dot measures the words themselves —
                                the <h1> box is the full half-width column, which would aim
                                it far to the right of the letters. */}
                            <span ref={titleRef}>
                                {title.scriptFirst ? scriptWord : serifWord}
                                <br/>
                                {title.scriptFirst ? serifWord : scriptWord}
                            </span>
                        </h1>
                    </div>

                    {/* On desktop this column is pinned to the container's right edge and
                        given the bottom half of the panel as its box, so the text clears
                        the subject of the background photo on both axes. */}
                    <div className="lg:w-1/3 lg:ml-auto lg:self-end lg:h-1/2 lg:flex lg:flex-col lg:justify-center">
                        <h2
                            ref={headingRef}
                            className={`text-left lg:text-right text-4xl lg:text-5xl font-bold mb-6 lg:mb-10 ${revealAt(HEADING_AT)}`}
                            style={{ fontFamily: '"Zaslia", serif', color: ink }}
                        >
                            {sections.length > 0
                                ? sections.map((section) => (
                                    <button
                                        key={section.key}
                                        onClick={section.onSelect}
                                        className="block w-full text-left lg:text-right hover:opacity-60 transition-opacity duration-300"
                                    >
                                        {section.label}
                                    </button>
                                ))
                                : heading}
                        </h2>
                        <p
                            ref={bodyRef}
                            className={`text-left lg:text-right text-base lg:text-sm font-light leading-relaxed mb-8 ${revealAt(BODY_AT)}`}
                            style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', color: ink }}
                        >
                            {body}
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
